/**
 * Feature-driven spectral analysis for the sonification engine.
 *
 * Works directly on a flux array (any of observed / interpolated / reddened),
 * so what is heard always derives from the spectrum being displayed rather
 * than from pre-computed catalogue measurements.
 */

export interface SpectralFeature {
  /** Peak centre in rest-frame Å. */
  lambdaA: number;
  /** Peak height above the estimated continuum, flux units. */
  prominence: number;
  /** Full width at half prominence, Å. */
  fwhmA: number;
  /** Horizontal base distance: width where the excess falls to 10% of prominence, Å. */
  baseWidthA: number;
  /** Integrated excess above continuum across the base, flux × Å (≈ line flux). */
  area: number;
  /** Prominence over the local median uncertainty. */
  snr: number;
  /** Manifest feature key (e.g. "Halpha") when the peak matches a known line. */
  knownKey: string | null;
  /** Rest wavelength of the matched known line, Å; null for discovery peaks. */
  restA: number | null;
}

export interface ContinuumSummary {
  /** Median continuum flux in the blue band (4300–4800 Å). NaN if no coverage. */
  blueLevel: number;
  /** Median continuum flux in the red band (6000–6800 Å). NaN if no coverage. */
  redLevel: number;
}

export interface FeatureAnalysis {
  features: SpectralFeature[];
  continuum: Float32Array;
  summary: ContinuumSummary;
}

/** Half-width of the running-median continuum window, Å. */
const CONTINUUM_WINDOW_A = 150;
/** Continuum is estimated on every Nth pixel and linearly interpolated. */
const CONTINUUM_DECIMATION = 8;

/**
 * User-tunable detection thresholds. The defaults are the historical
 * hardcoded behaviour; the mixer panel exposes these live.
 */
export interface ExtractionSettings {
  /** Peaks must exceed this many local uncertainty medians to count. */
  minSnr: number;
  /** Minimum base width (Å) — rejects single-pixel noise spikes. */
  minBaseWidthA: number;
  /** Maximum base width (Å) — rejects broad continuum undulations. */
  maxBaseWidthA: number;
  /** Peaks this close (Å) to the epoch's coverage edges are ignored. */
  edgeMarginA: number;
  /** A peak matches a known line when within max(this, 0.6 × FWHM) of it, Å. */
  knownMatchToleranceA: number;
  /** Cap on unidentified features kept (strongest first). */
  maxDiscoveryFeatures: number;
}

export const DEFAULT_EXTRACTION_SETTINGS: ExtractionSettings = {
  minSnr: 5,
  minBaseWidthA: 12,
  maxBaseWidthA: 500,
  edgeMarginA: 80,
  knownMatchToleranceA: 18,
  maxDiscoveryFeatures: 10,
};

function median(values: number[]): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : 0.5 * (sorted[mid - 1] + sorted[mid]);
}

/**
 * Running-median continuum estimate. Computed on a decimated grid for speed
 * (the scrub path re-runs this every frame) and linearly interpolated back to
 * full resolution. NaN inputs stay NaN in the output.
 */
export function estimateContinuum(flux: Float32Array, stepA: number): Float32Array {
  const n = flux.length;
  const out = new Float32Array(n);
  out.fill(NaN);
  const halfWindowPx = Math.max(4, Math.round(CONTINUUM_WINDOW_A / stepA));

  const anchorIdx: number[] = [];
  const anchorVal: number[] = [];
  for (let i = 0; i < n; i += CONTINUUM_DECIMATION) {
    if (!Number.isFinite(flux[i])) continue;
    const lo = Math.max(0, i - halfWindowPx);
    const hi = Math.min(n - 1, i + halfWindowPx);
    const windowVals: number[] = [];
    for (let k = lo; k <= hi; k += 2) {
      const v = flux[k];
      if (Number.isFinite(v)) windowVals.push(v);
    }
    if (windowVals.length < 4) continue;
    anchorIdx.push(i);
    anchorVal.push(median(windowVals));
  }
  if (anchorIdx.length < 2) return out;

  let a = 0;
  for (let i = 0; i < n; i += 1) {
    if (!Number.isFinite(flux[i])) continue;
    while (a < anchorIdx.length - 2 && anchorIdx[a + 1] < i) a += 1;
    const i0 = anchorIdx[a];
    const i1 = anchorIdx[a + 1];
    const t = Math.max(0, Math.min(1, (i - i0) / (i1 - i0)));
    out[i] = anchorVal[a] * (1 - t) + anchorVal[a + 1] * t;
  }
  return out;
}

function bandMedian(
  wavelength: Float32Array,
  values: Float32Array,
  loA: number,
  hiA: number,
): number {
  const collected: number[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const l = wavelength[i];
    if (l < loA || l > hiA) continue;
    const v = values[i];
    if (Number.isFinite(v)) collected.push(v);
  }
  return median(collected);
}

/**
 * Median flux in the 5050–5150 Å continuum window — the sonification's
 * per-epoch continuum level, normalised by the caller against the object's
 * brightest epoch.
 */
export function continuumLevel5100(wavelength: Float32Array, flux: Float32Array): number {
  return bandMedian(wavelength, flux, 5050, 5150);
}

/** Excess above continuum (NaN → 0 so coverage gaps cannot host a peak) and a 5-pixel boxcar of it. */
function excessAboveContinuum(flux: Float32Array, continuum: Float32Array): { excess: Float32Array; smooth: Float32Array } {
  const n = flux.length;
  const excess = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    const e = flux[i] - continuum[i];
    excess[i] = Number.isFinite(e) ? e : 0;
  }
  const smooth = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    let sum = 0;
    let count = 0;
    for (let k = Math.max(0, i - 2); k <= Math.min(n - 1, i + 2); k += 1) {
      sum += excess[k];
      count += 1;
    }
    smooth[i] = sum / count;
  }
  return { excess, smooth };
}

/** Wavelength bounds inside which peaks are trusted: coverage edges pulled in by the margin. */
function trustedRange(wavelength: Float32Array, flux: Float32Array, edgeMarginA: number): [number, number] {
  const n = flux.length;
  let firstFinite = 0;
  while (firstFinite < n && !Number.isFinite(flux[firstFinite])) firstFinite += 1;
  let lastFinite = n - 1;
  while (lastFinite > 0 && !Number.isFinite(flux[lastFinite])) lastFinite -= 1;
  return [
    wavelength[Math.min(firstFinite, n - 1)] + edgeMarginA,
    wavelength[Math.max(lastFinite, 0)] - edgeMarginA,
  ];
}

/**
 * Local maxima of the smoothed excess with sufficient prominence against the
 * uncertainty array and a base width inside the accepted range. Peaks come
 * back unlabelled, in wavelength order.
 */
function scanPeaks(
  wavelength: Float32Array,
  excess: Float32Array,
  smooth: Float32Array,
  uncertainty: Float32Array | null,
  stepA: number,
  [loEdgeA, hiEdgeA]: [number, number],
  settings: ExtractionSettings,
): SpectralFeature[] {
  const n = smooth.length;
  const globalNoise = uncertainty
    ? bandMedian(wavelength, uncertainty, wavelength[0], wavelength[n - 1])
    : NaN;
  const noiseFloor = Number.isFinite(globalNoise) && globalNoise > 0 ? globalNoise : 0.5;

  const peaks: SpectralFeature[] = [];
  for (let i = 2; i < n - 2; i += 1) {
    const v = smooth[i];
    if (v <= 0) continue;
    if (wavelength[i] < loEdgeA || wavelength[i] > hiEdgeA) continue;
    if (v < smooth[i - 1] || v <= smooth[i + 1]) continue;
    if (v < smooth[i - 2] || v < smooth[i + 2]) continue;

    // Prominence: drop to the higher of the two flanking minima, searching
    // outwards until the excess climbs back above the peak or the array ends.
    let leftMin = v;
    for (let k = i - 1; k >= 0; k -= 1) {
      if (smooth[k] > v) break;
      if (smooth[k] < leftMin) leftMin = smooth[k];
    }
    let rightMin = v;
    for (let k = i + 1; k < n; k += 1) {
      if (smooth[k] > v) break;
      if (smooth[k] < rightMin) rightMin = smooth[k];
    }
    const prominence = v - Math.max(leftMin, rightMin);
    if (prominence <= 0) continue;

    const localNoise = uncertainty
      ? bandMedian(wavelength, uncertainty, wavelength[i] - 60, wavelength[i] + 60)
      : noiseFloor;
    const noise = Number.isFinite(localNoise) && localNoise > 0 ? localNoise : noiseFloor;
    const snr = prominence / noise;
    if (snr < settings.minSnr) continue;

    // Half-prominence and base (10% prominence) crossings.
    const half = v - prominence * 0.5;
    const base = v - prominence * 0.9;
    let halfLeft = i;
    let baseLeft = i;
    for (let k = i - 1; k >= 0; k -= 1) {
      if (smooth[k] > v) break;
      if (smooth[k] > half) halfLeft = k;
      if (smooth[k] > base) baseLeft = k;
      else break;
    }
    let halfRight = i;
    let baseRight = i;
    for (let k = i + 1; k < n; k += 1) {
      if (smooth[k] > v) break;
      if (smooth[k] > half) halfRight = k;
      if (smooth[k] > base) baseRight = k;
      else break;
    }
    const fwhmA = (halfRight - halfLeft + 1) * stepA;
    const baseWidthA = (baseRight - baseLeft + 1) * stepA;
    if (baseWidthA < settings.minBaseWidthA || baseWidthA > settings.maxBaseWidthA) continue;

    let area = 0;
    for (let k = baseLeft; k <= baseRight; k += 1) {
      if (excess[k] > 0) area += excess[k] * stepA;
    }

    peaks.push({ lambdaA: wavelength[i], prominence, fwhmA, baseWidthA, area, snr, knownKey: null, restA: null });
  }
  return peaks;
}

/**
 * Flat-topped broad lines produce several maxima; keep the more prominent of
 * any pair closer than half the narrower FWHM. Returns strongest first.
 */
function mergeNearDuplicates(candidates: SpectralFeature[], stepA: number): SpectralFeature[] {
  const byProminence = [...candidates].sort((f1, f2) => f2.prominence - f1.prominence);
  const merged: SpectralFeature[] = [];
  for (const c of byProminence) {
    const clash = merged.some(
      (m) => Math.abs(m.lambdaA - c.lambdaA) < 0.5 * Math.min(m.fwhmA, c.fwhmA) + stepA * 2,
    );
    if (!clash) merged.push(c);
  }
  return merged;
}

/**
 * Label each peak with the nearest known line inside tolerance, then let one
 * feature per known key keep the claim (strongest wins; the rest revert to
 * discovery). Mutates the features in place; expects strongest-first order.
 */
function labelKnownLines(
  features: SpectralFeature[],
  knownLines: Record<string, number>,
  toleranceA: number,
): void {
  for (const feature of features) {
    let bestKey: string | null = null;
    let bestRestA: number | null = null;
    let bestDist = Infinity;
    for (const [key, restA] of Object.entries(knownLines)) {
      const dist = Math.abs(feature.lambdaA - restA);
      const tolerance = Math.max(toleranceA, 0.6 * feature.fwhmA);
      if (dist < tolerance && dist < bestDist) {
        bestDist = dist;
        bestKey = key;
        bestRestA = restA;
      }
    }
    feature.knownKey = bestKey;
    feature.restA = bestRestA;
  }
  const claimed = new Map<string, SpectralFeature>();
  for (const feature of features) {
    if (!feature.knownKey) continue;
    const holder = claimed.get(feature.knownKey);
    if (!holder || feature.prominence > holder.prominence) {
      if (holder) {
        holder.knownKey = null;
        holder.restA = null;
      }
      claimed.set(feature.knownKey, feature);
    } else {
      feature.knownKey = null;
      feature.restA = null;
    }
  }
}

/**
 * Detect emission features in a spectrum. Peaks near a known line wavelength
 * are labelled with the manifest feature key; the rest are "discovery"
 * features, capped at the strongest few.
 */
export function analyzeSpectrum(
  wavelength: Float32Array,
  flux: Float32Array,
  uncertainty: Float32Array | null,
  stepA: number,
  knownLines: Record<string, number>,
  settings: ExtractionSettings = DEFAULT_EXTRACTION_SETTINGS,
): FeatureAnalysis {
  const continuum = estimateContinuum(flux, stepA);
  const { excess, smooth } = excessAboveContinuum(flux, continuum);
  const range = trustedRange(wavelength, flux, settings.edgeMarginA);
  const peaks = scanPeaks(wavelength, excess, smooth, uncertainty, stepA, range, settings);
  const merged = mergeNearDuplicates(peaks, stepA);
  labelKnownLines(merged, knownLines, settings.knownMatchToleranceA);

  const known = merged.filter((f) => f.knownKey !== null);
  const discovery = merged
    .filter((f) => f.knownKey === null)
    .slice(0, Math.max(0, Math.round(settings.maxDiscoveryFeatures)));
  const features = [...known, ...discovery].sort((f1, f2) => f1.lambdaA - f2.lambdaA);

  return {
    features,
    continuum,
    summary: {
      blueLevel: bandMedian(wavelength, continuum, 4300, 4800),
      redLevel: bandMedian(wavelength, continuum, 6000, 6800),
    },
  };
}
